import { groupByEquality, partition, selectMany } from "../../omnia"
import { Class, IMapTopologyGeometry, IMapTopologyService } from "./types"

interface Zone {
    geometry: IMapTopologyGeometry,
    cls: Class,
    area?: number
}

export function mergeSmallZones(topo: IMapTopologyService, zones: Zone[], minArea: number) {

    zones = zones.map(z => ({
        ...z,
        area: z.geometry.getArea()
    }))

    return merge(topo, zones, minArea)

}

function merge(topo: IMapTopologyService, zones: Zone[], minArea: number) {

    const [small, large] = partition(zones, z => z.area < minArea)

    const assoc = small
        .map(s => ({
            source: s,
            target: getLargestAdjacent(topo, s, large)
        }))
        .filter(i => i.target)

    const others = zones.filter(z => assoc.every(m => (z != m.source) && (z != m.target)))

    const groups = groupByEquality(assoc, m => m.target)

    const merged = selectMany(groups.map(g => {

        const zones = [g.key].concat(g.items.map(i => i.source))
        const m = topo.union(zones.map(i => i.geometry))

        return m && m.isValid() && (m.getDimension() == 2)
            ? [{
                geometry: m,
                cls: g.key.cls,
                area: m.getArea()
            }]
            : zones
    }))

    const result = merged.concat(others)

    return (result.length == zones.length)
        ? zones
        : merge(topo, result, minArea)

}

function getLargestAdjacent(topo: IMapTopologyService, small: Zone, others: Zone[]) {

    const matches = others
        .filter(o => !topo.disjoint(small.geometry, o.geometry))
        .map(o => {
            const intersection = topo.intersection(o.geometry, small.geometry)
            const dim = intersection ? intersection.getDimension() : 0
            if (dim < 1) return null
            return {
                target: o,
                intersection,
                dim,
                area: dim == 2 ? intersection.getArea() : 0,
                length: dim == 1 ? intersection.getLength() : 0
            }
        })
        .filter(i => !!i)
        .sort((a, b) => (b.area - a.area) || (b.length - a.length) || (b.target.area - a.target.area))

    return matches.length ? matches[0].target : null

}
